import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { MerSETASDIService } from '../../../../../../Component/Api/merSETA/Internal/MerSETASDIService';
import { DataTable } from '../../../../../../Component/Common/Components/DataTable/DataTable';
import { ColumnSortOption } from '../../../../../../Component/Model/Dto/ColumnSortOption';
import { MerSetaSDITrainerSearchInput, MerSetaSDITrainerSearchResult, MerSetaSdi } from '../../../../../../Component/Model/Dto/WSP/MerSETASDI';
import { EntriesPerPage } from '../../../../../../Component/Model/Table/Table';
import { OrganisationAtom } from '../../../../../../State/Organisation.Atom';
import { InterventionAtom } from '../../Intervention.Atom';
import { InternalTrainersModal } from './InternalTrainersModal';
import { MerSetaSDITableMapper } from './InternalTrainersTableColumn';
import { ConfirmationDialog } from '../../../../../../Component/Common/Components/ConfirmationDialog';
import { toast } from 'react-toastify';

export const InternalTrainers = () => {
    const selectedSDI = useRecoilValue(InterventionAtom);
    const peopleAndTrainingOrganisation = useRecoilValue(OrganisationAtom);

    const [isLoading, setIsLoading] = useState(false);
    const [trainers, setTrainers] = useState<MerSetaSDITrainerSearchResult[]>([]);

    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>('');
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);

    const [merSetaSDI, setMerSetaSDI] = useState<MerSetaSdi | undefined>(undefined);
    const [selectedPlatformPeopleId, setSelectedPlatformPeopleId] = useState<string | undefined>();
    const [selectedTrainerName, setSelectedTrainerName] = useState('');
    const [showInternalTrainersModal, setShowInternalTrainersModal] = useState(false);

    const [selectedTrainerId, setSelectedTrainerId] = useState(-1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteMessage, setConfirmDeleteMessage] = useState("");

    const merSetaSDITrainerSearchRequest: MerSetaSDITrainerSearchInput = {
        organisationId: peopleAndTrainingOrganisation.OrganisationId,
        merSetaSDIId: selectedSDI.id,
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue as number,
        searchValue: searchValue,
        columnSortOptions: columnSortOptions,
    };

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([...columnSortOptions, sortOption]);
        setPageNumber(1);
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await MerSETASDIService.TrainerSearch(merSetaSDITrainerSearchRequest);
            setTrainers(response.data.results);
            setFilteredItemsCount(response.data.filteredItemsCount);
            setTotalItemsCount(response.data.totalItemsCount);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (selectedSDI.id > 0 && peopleAndTrainingOrganisation.OrganisationId > 0) {
            fetchData();
        }
    }, [selectedSDI.id, peopleAndTrainingOrganisation.OrganisationId, searchValue, entriesPerPage, pageNumber, columnSortOptions]);

    const getMersetaRecord = async () => {
        setIsLoading(true);
        try {
            const result = await MerSETASDIService.GetById(selectedSDI.id);
            setMerSetaSDI(result.data);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (selectedSDI.id > 0) {
            getMersetaRecord();
        }
    }, [selectedSDI.id, peopleAndTrainingOrganisation.OrganisationId]);

    const handleEdit = (platformPeopleId: string, fullName: string) => {
        setSelectedPlatformPeopleId(platformPeopleId);
        setSelectedTrainerName(fullName);
        setShowInternalTrainersModal(true);
    }

    const showDeleteConfirmationModal = (id: number, fullName: string) => {
        setConfirmDeleteMessage(`Are you sure you want to remove ${fullName} from this training intervention?`);
        setSelectedTrainerId(id);
        setShowDeleteModal(true);
    }

    const closeDeleteConfirmationModal = () => {
        setShowDeleteModal(false);
        setConfirmDeleteMessage("");
        setSelectedTrainerId(-1);
        fetchData();
    }

    const deleteTrainer = async () => {
        try {
            if (selectedTrainerId > 0 && selectedSDI.id > 0) {
                await MerSETASDIService.DeleteTrainer(selectedTrainerId, selectedSDI.id);
                toast.success("Successfully deleted trainer");
                closeDeleteConfirmationModal();
            }
        } catch (error) {
            console.log(error);
        }
    }

    const handleAddTrainer = () => {
        resetTrainerModal();
        setShowInternalTrainersModal(true);
    }

    const handleCloseInternalTrainerModal = () => {
        resetTrainerModal();
        setShowInternalTrainersModal(false);
        fetchData();
    }

    const resetTrainerModal = () => {
        setSelectedPlatformPeopleId(undefined);
        setSelectedTrainerName("");
    }

    const bulkActions = [
        {
            label: "Capture Trainer",
            action: handleAddTrainer
        }
    ];

    const tableData = MerSetaSDITableMapper(trainers, handleEdit, showDeleteConfirmationModal);

    return (
        <>
            <DataTable
                canSearch
                key={totalItemsCount}
                table={tableData}
                entriesPerPage={entriesPerPage}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumber={pageNumber}
                pageNumberChanged={pageNumberChanged}
                columnSortingChanged={columnSortingChanged}
                searchValueChanged={searchValueChanged}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                isLoading={isLoading}
                manualSorting={true}
                bulkActions={bulkActions}
                searchBoxText='Search Trainers...'
            />

            {
                showInternalTrainersModal && merSetaSDI != null &&
                <InternalTrainersModal
                    key={selectedPlatformPeopleId}
                    selectedPlatformPeopleId={selectedPlatformPeopleId}
                    selectedTrainerName={selectedTrainerName}
                    handleClose={handleCloseInternalTrainerModal}
                    merSetaSDI={merSetaSDI}
                    showModal={showInternalTrainersModal}
                />
            }
            {showDeleteModal &&
                <ConfirmationDialog
                    showDialog={showDeleteModal}
                    handleAccept={deleteTrainer}
                    handleCancel={closeDeleteConfirmationModal}
                    heading={"Delete Trainer"}
                    message={deleteMessage}
                />
            }
        </>
    )
}
